import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import QRCode from 'qrcode.react';

const PrintCustom = () => {
  const history = useHistory();
  const location = useLocation();
  const selectedIds = location.state?.selected || [];

  const allProducts = useSelector((state) => state.products.products);
  const token = useSelector((state) => state.user.token);

  const [allTypes, setAllTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        console.log('Fetching types from /api/types/all_types');
        const res = await axios.get('/api/types/all_types');
        console.log('Types API response (raw):', res.data);
        let typesArray = [];
        if (res.data && typeof res.data === 'object') {
          if (Array.isArray(res.data)) {
            typesArray = res.data;
            console.log('Response is already an array');
          } else {
            console.log('Converting object response to array...');
            typesArray = Object.keys(res.data)
              .map((key) => res.data[key])
              .filter((item) => item && typeof item === 'object');
            console.log('Converted to array:', typesArray);
          }
        }
        if (typesArray.length > 0) {
          setAllTypes(typesArray);
          console.log(`Successfully loaded ${typesArray.length} types`);
          console.log('Type mapping:');
          typesArray.forEach((type) => {
            console.log(`${type.type_id} -> ${type.name}`);
          });
        } else {
          console.error('No types found after processing');
          setAllTypes([]);
        }
      } catch (err) {
        console.error('Failed to fetch types:', err);
        setAllTypes([]);
      }
    };

    fetchTypes();
  }, []);

  const findProductType = (typeCode) => {
    if (!allTypes || allTypes.length === 0) {
      console.log(`No types loaded. Looking for: "${typeCode}"`);
      return typeCode || '';
    }

    if (!typeCode) {
      console.log('Empty type code provided');
      return '';
    }

    console.log(`Looking for type: "${typeCode}"`);
    console.log(
      `Available types (${allTypes.length}):`,
      allTypes.map((t) => `${t.type_id}=${t.name}`),
    );

    const productType = allTypes.find((type) => {
      const match = String(type.type_id) === String(typeCode);
      if (match) {
        console.log(`FOUND MATCH! ${type.type_id} = ${type.name}`);
      }
      return match;
    });

    const result = productType ? productType.name : typeCode;
    console.log(`Result for "${typeCode}": "${result}"`);
    return result;
  };

  const resolveFromRedux = () => {
    const resolved = selectedIds
      .map((id) => allProducts.find((p) => p._id === id))
      .filter(Boolean);
    return resolved.length === selectedIds.length ? resolved : null;
  };

  const fetchFromAPI = async () => {
    try {
      const res = await axios.post(
        '/api/products/get_many',
        { ids: selectedIds },
        { headers: { Authorization: token } },
      );
      return res.data;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const calculateM2 = (p) =>
    ((p.dimensions.short * p.dimensions.long) / 10000).toFixed(3);

  const defaultComment = (p) =>
    p.comments?.trim() !== '' ? p.comments : `box-${p._id.slice(-2)}`;

  // Load data
  useEffect(() => {
    const load = async () => {
      if (!selectedIds.length) {
        setLoading(false);
        return;
      }

      let data = resolveFromRedux();
      if (!data || data.length === 0) {
        data = await fetchFromAPI();
      }

      if (!data || data.length === 0) {
        console.error('No data loaded');
        setLoading(false);
        return;
      }

      console.log('Loaded products:', data);

      const init = {};
      data.forEach((p) => {
        const typeCode = p.product?.product_type || '';
        const typeName = findProductType(typeCode);

        console.log(`Processing product ${p._id}:`, {
          typeCode,
          typeName,
          productData: p.product,
        });

        init[p._id] = {
          typeCode: typeCode,
          typeName: typeName,
          comment: defaultComment(p),
          short: p.dimensions?.short || 0,
          long: p.dimensions?.long || 0,
          width: p.dimensions?.width || 0,
          m2: calculateM2(p),
          amount: p.amount || 1,
        };
      });

      console.log('FormData initialized:', init);
      setFormData(init);
      setItems(data);
      setLoading(false);
    };

    load();
  }, [selectedIds, allTypes]);

  const changeField = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };
  const generateLabelXml = () => {
    if (!items || items.length === 0) return '';

    // Base constants - adjusted for proper sizing
    const QR_WIDTH = 52;
    const FRAME_HEIGHT = 51.2;
    const GAP_AFTER_QR = 3;
    const TEXT_RIGHT_MARGIN = 20; // Increased from 10
    const LABEL_SPACING = 10;

    // More accurate character width (fonts are wider than we calculated)
    const CHAR_WIDTH = 5.8; // Increased from 4.2

    // Minimum card width for short text
    const MIN_CARD_WIDTH = 190;

    let objects = '';
    let currentX = 5.6;

    items.slice(0, 10).forEach((p, i) => {
      const f = formData[p._id];
      if (!f) return;

      const url = `https://app.donstone.lt/product_view/${p._id}`;

      // Get the text
      const typeName = f.typeName || f.typeCode || '';
      const comment = f.comment || '';
      const lineA = `${typeName} (${comment})`;
      const lineB = `${f.long}x${f.short}x${f.width} (${f.m2})`;

      // Find the LONGEST line
      const longestLine = Math.max(lineA.length, lineB.length);

      // Calculate width needed with more accurate char width
      const textWidthNeeded = longestLine * CHAR_WIDTH;
      const calculatedWidth =
        QR_WIDTH + GAP_AFTER_QR + textWidthNeeded + TEXT_RIGHT_MARGIN;

      // Use calculated width but ensure minimum size
      const frameWidth = Math.max(calculatedWidth, MIN_CARD_WIDTH);

      // Positions
      const frameX = currentX;
      const qrX = frameX + 5;
      const qrY = 12;

      // Text starts right after QR
      const textX = qrX + QR_WIDTH + GAP_AFTER_QR;

      // Text width = frame width minus QR and margins
      const textWidth = frameWidth - QR_WIDTH - 5 - TEXT_RIGHT_MARGIN;

      // Text Y positions
      const lineAY = 16;
      const lineBY = 36.5;

      objects += `
<draw:frame>
  <pt:objectStyle x="${frameX}pt" y="8.4pt" width="${frameWidth}pt" height="${FRAME_HEIGHT}pt" backColor="#FFFFFF" backPrintColorNumber="0" ropMode="COPYPEN" angle="0" anchor="TOPLEFT" flip="NONE">
    <pt:pen style="INSIDEFRAME" widthX="0.5pt" widthY="0.5pt" color="#000000" printColorNumber="1"/>
    <pt:brush style="NULL" color="#000000" printColorNumber="1" id="0"/>
    <pt:expanded objectName="Frame${
      i + 1
    }" ID="${i}" lock="2" templateMergeTarget="LABELLIST" templateMergeType="NONE" templateMergeID="0" allowOutOfBoundsTransfer="false" linkStatus="NONE" linkID="0"/>
  </pt:objectStyle>
  <draw:frameStyle category="SIMPLE" style="0" stretchCenter="true"/>
</draw:frame>

<barcode:barcode>
  <pt:objectStyle x="${qrX}pt" y="${qrY}pt" width="49.2pt" height="49.2pt" backColor="#FFFFFF" backPrintColorNumber="0" ropMode="COPYPEN" angle="0" anchor="TOPLEFT" flip="NONE">
    <pt:pen style="INSIDEFRAME" widthX="0.5pt" widthY="0.5pt" color="#000000" printColorNumber="1"/>
    <pt:brush style="NULL" color="#000000" printColorNumber="1" id="0"/>
    <pt:expanded objectName="Barcode${
      i + 1
    }" ID="${i}" lock="0" templateMergeTarget="LABELLIST" templateMergeType="NONE" templateMergeID="0" allowOutOfBoundsTransfer="false" linkStatus="NONE" linkID="0"/>
  </pt:objectStyle>
  <barcode:barcodeStyle protocol="QRCODE" lengths="48" zeroFill="false" barWidth="1.2pt" barRatio="1:3" humanReadable="true" humanReadableAlignment="LEFT" checkDigit="false" autoLengths="true" margin="true" sameLengthBar="false" bearerBar="false"/>
  <barcode:qrcodeStyle model="2" eccLevel="15%" cellSize="1.2pt" mbcs="auto" joint="1" version="auto"/>
  <pt:data>${url}</pt:data>
</barcode:barcode>

<text:text>
  <pt:objectStyle x="${textX}pt" y="${lineAY}pt" width="${textWidth}pt" height="14.4pt" backColor="#FFFFFF" backPrintColorNumber="0" ropMode="COPYPEN" angle="0" anchor="TOPLEFT" flip="NONE">
    <pt:pen style="NULL" widthX="0.5pt" widthY="0.5pt" color="#000000" printColorNumber="1"/>
    <pt:brush style="NULL" color="#000000" printColorNumber="1" id="0"/>
    <pt:expanded objectName="Text${
      i + 1
    }a" ID="${i}" lock="0" templateMergeTarget="LABELLIST" templateMergeType="NONE" templateMergeID="0" allowOutOfBoundsTransfer="false" linkStatus="NONE" linkID="0"/>
  </pt:objectStyle>
  <text:ptFontInfo>
    <text:logFont name="Times New Roman" width="0" italic="false" weight="700" charSet="0" pitchAndFamily="2"/>
    <text:fontExt effect="NOEFFECT" underline="0" strikeout="0" size="13pt" orgSize="31.2pt" textColor="#000000" textPrintColorNumber="1"/>
  </text:ptFontInfo>
  <text:textControl control="AUTOLEN" clipFrame="false" aspectNormal="true" shrink="true" autoLF="false" avoidImage="false"/>
  <text:textAlign horizontalAlignment="LEFT" verticalAlignment="TOP" inLineAlignment="BASELINE"/>
  <text:textStyle vertical="false" nullBlock="false" charSpace="0" lineSpace="0" orgPoint="136pt" combinedChars="false"/>
  <text:transferSettings editOnPrintFormat="" editOnPrintOrder="0"/>
  <pt:data>${lineA}</pt:data>
  <text:stringItem charLen="${lineA.length}">
    <text:ptFontInfo>
      <text:logFont name="Times New Roman" width="0" italic="false" weight="700" charSet="0" pitchAndFamily="2"/>
      <text:fontExt effect="NOEFFECT" underline="0" strikeout="0" size="13pt" orgSize="31.2pt" textColor="#000000" textPrintColorNumber="1"/>
    </text:ptFontInfo>
  </text:stringItem>
</text:text>

<text:text>
  <pt:objectStyle x="${textX}pt" y="${lineBY}pt" width="${textWidth}pt" height="15.5pt" backColor="#FFFFFF" backPrintColorNumber="0" ropMode="COPYPEN" angle="0" anchor="TOPLEFT" flip="NONE">
    <pt:pen style="NULL" widthX="0.5pt" widthY="0.5pt" color="#000000" printColorNumber="1"/>
    <pt:brush style="NULL" color="#000000" printColorNumber="1" id="0"/>
    <pt:expanded objectName="Text${
      i + 1
    }b" ID="${i}" lock="0" templateMergeTarget="LABELLIST" templateMergeType="NONE" templateMergeID="0" allowOutOfBoundsTransfer="false" linkStatus="NONE" linkID="0"/>
  </pt:objectStyle>
  <text:ptFontInfo>
    <text:logFont name="Times New Roman" width="0" italic="false" weight="700" charSet="0" pitchAndFamily="2"/>
    <text:fontExt effect="NOEFFECT" underline="0" strikeout="0" size="14pt" orgSize="33.6pt" textColor="#000000" textPrintColorNumber="1"/>
  </text:ptFontInfo>
  <text:textControl control="AUTOLEN" clipFrame="false" aspectNormal="true" shrink="true" autoLF="false" avoidImage="false"/>
  <text:textAlign horizontalAlignment="LEFT" verticalAlignment="TOP" inLineAlignment="BASELINE"/>
  <text:textStyle vertical="false" nullBlock="false" charSpace="0" lineSpace="0" orgPoint="14pt" combinedChars="false"/>
  <text:transferSettings editOnPrintFormat="" editOnPrintOrder="0"/>
  <pt:data>${lineB}</pt:data>
  <text:stringItem charLen="${lineB.length}">
    <text:ptFontInfo>
      <text:logFont name="Times New Roman" width="0" italic="false" weight="700" charSet="0" pitchAndFamily="2"/>
      <text:fontExt effect="NOEFFECT" underline="0" strikeout="0" size="14pt" orgSize="33.6pt" textColor="#000000" textPrintColorNumber="1"/>
    </text:ptFontInfo>
  </text:stringItem>
</text:text>
`;

      currentX += frameWidth + LABEL_SPACING;
    });

    const totalWidth = currentX + 5.6;

    return `<?xml version="1.0" encoding="UTF-8"?>
<pt:document xmlns:pt="http://schemas.brother.info/ptouch/2007/lbx/main"
             xmlns:style="http://schemas.brother.info/ptouch/2007/lbx/style"
             xmlns:text="http://schemas.brother.info/ptouch/2007/lbx/text"
             xmlns:draw="http://schemas.brother.info/ptouch/2007/lbx/draw"
             xmlns:barcode="http://schemas.brother.info/ptouch/2007/lbx/barcode"
             version="1.10"
             generator="com.brother.PtouchEditor">
  <pt:body currentSheet="Sheet 1" direction="LTR">
    <style:sheet name="Sheet 1">
      <style:paper media="0" width="68pt" height="2387.2pt" marginLeft="8.4pt" marginTop="5.6pt" marginRight="8.4pt" marginBottom="5.6pt" orientation="landscape" autoLength="false" monochromeDisplay="true" printColorDisplay="false" printColorsID="0" paperColor="#FFFFFF" paperInk="#000000" split="1" format="261" backgroundTheme="0" printerID="26672" printerName="Brother PT-P750W"/>
      <style:cutLine regularCut="0pt" freeCut=""/>
      <style:backGround x="5.6pt" y="8.4pt" width="${totalWidth}pt" height="51.2pt" brushStyle="NULL" brushId="0" userPattern="NONE" userPatternId="0" color="#000000" printColorNumber="1" backColor="#FFFFFF" backPrintColorNumber="0"/>
      <pt:objects>
        ${objects}
      </pt:objects>
    </style:sheet>
  </pt:body>
</pt:document>`;
  };
  const downloadLBX = async () => {
    try {
      const labelXml = generateLabelXml();

      const res = await axios.post(
        '/api/print/generate',
        { labelXml },
        { responseType: 'blob' },
      );

      const blob = new Blob([res.data], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `labels_${Date.now()}.lbx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to generate LBX file. Check console for details.');
    }
  };

  const printLBX = async () => {
    try {
      const labelXml = generateLabelXml();

      await axios.post('http://localhost:7000/print', {
        labelXml,
      });

      alert('Print command sent to local server');
    } catch (err) {
      console.error('Print failed:', err);
      alert(
        'Failed to send print command. Make sure local server is running on port 7000.',
      );
    }
  };

  const markPrinted = async () => {
    try {
      await axios.patch(
        '/api/products/mark_printed',
        { ids: selectedIds },
        { headers: { 'auth-token': token } },
      );
      //   history.push('/home');
    } catch (err) {
      alert('Failed to update print date.');
    }
  };

  const viewXml = () => {
    const xml = generateLabelXml();
    console.log(xml);

    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'debug_label.xml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const debugState = () => {
    console.log('=== DEBUG STATE ===');
    console.log('allTypes:', allTypes);
    console.log('items count:', items.length);
    console.log('formData:', formData);

    if (items.length > 0) {
      console.log('\n=== PRODUCT TYPE MAPPING ===');
      items.forEach((p, i) => {
        const f = formData[p._id];
        const typeCode = p.product?.product_type;
        const foundName = findProductType(typeCode);
        console.log(`Product ${i + 1}:`);
        console.log(`  ID: ${p._id}`);
        console.log(`  Type code from DB: "${typeCode}"`);
        console.log(`  Type name in form: "${f?.typeName}"`);
        console.log(`  findProductType("${typeCode}") = "${foundName}"`);
        console.log(`  Should show: "${foundName}" not "${typeCode}"`);
      });
    }
  };

  if (loading) return <h2>Loading…</h2>;

  if (items.length === 0) return <h2>No products selected</h2>;

  return (
    <Wrapper>
      <h1>Print Labels ({items.length} products)</h1>
      {/* 
      <DebugButton onClick={debugState}>Debug State</DebugButton>
      <TypeStatus>
        {allTypes.length > 0
          ? `✓ Loaded ${allTypes.length} types (including: ${allTypes
              .slice(0, 3)
              .map((t) => t.name)
              .join(', ')})`
          : '⏳ Loading types...'}
      </TypeStatus> */}

      <Table>
        <thead>
          <tr>
            <th>Type</th>
            <th>Comment</th>
            <th>Long</th>
            <th>Short</th>
            <th>Width</th>
            <th>m²</th>
            <th>Amount</th>
          </tr>
        </thead>

        <tbody>
          {items.map((p) => {
            const f = formData[p._id];
            if (!f) return null;

            const isNameDifferent = f.typeName && f.typeName !== f.typeCode;
            const displayName = f.typeName || f.typeCode || '';

            return (
              <tr key={p._id}>
                <td>
                  <TypeInput
                    value={displayName}
                    onChange={(e) => changeField(p._id, 'typeName', e.target.value)}
                    placeholder="Type name"
                    $isName={isNameDifferent}
                  />
                  <TypeCode>Code: {f.typeCode || 'none'}</TypeCode>
                </td>
                <td>
                  <input
                    value={f.comment || ''}
                    onChange={(e) => changeField(p._id, 'comment', e.target.value)}
                    placeholder="Comment"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={f.long || ''}
                    onChange={(e) => changeField(p._id, 'long', Number(e.target.value))}
                    placeholder="Long"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={f.short || ''}
                    onChange={(e) => changeField(p._id, 'short', Number(e.target.value))}
                    placeholder="Short"
                  />
                </td>

                <td>
                  <input
                    type="number"
                    value={f.width || ''}
                    onChange={(e) => changeField(p._id, 'width', Number(e.target.value))}
                    placeholder="Width"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={f.m2 || ''}
                    onChange={(e) => changeField(p._id, 'm2', e.target.value)}
                    placeholder="Value"
                    style={{ textAlign: 'center' }}
                  />
                </td>
                <td>
                  <div style={{ padding: '8px 0' }}>{f.amount}</div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <PreviewSection>
        <h3 style={{ marginBottom: '8px' }}>Label Preview:</h3>

        <LabelRow>
          {items.map((p) => {
            const f = formData[p._id];
            if (!f) return null;

            const url = `https://app.donstone.lt/product_view/${p._id}`;

            return (
              <MiniLabel key={p._id}>
                <MiniQR>
                  <QRCode value={url} size={75} level="H" includeMargin={false} />
                </MiniQR>

                <MiniText>
                  <div className="title">
                    {f.typeName || f.typeCode} ({f.comment})
                  </div>
                  <div className="details">
                    {f.long}x{f.short}x{f.width} ({f.m2})
                  </div>
                </MiniText>
              </MiniLabel>
            );
          })}
        </LabelRow>
      </PreviewSection>

      <Buttons>
        <button className="btn-cancel" onClick={() => history.push('/home')}>
          Cancel
        </button>
        {/* <button className="btn-debug" onClick={viewXml}>
          Debug XML
        </button> */}
        <button className="btn-download" onClick={downloadLBX}>
          Download LBX
        </button>
        {/* <button className="btn-print" onClick={printLBX}>
          Print via Local
        </button> */}
        <button className="btn-save" onClick={markPrinted}>
          Save Print Date
        </button>
      </Buttons>
      {/* 
      <DebugInfo>
        <p>
          <strong>Type Mapping Status:</strong>{' '}
          {allTypes.length > 0 ? `${allTypes.length} types loaded` : 'No types loaded'}
        </p>
        {allTypes.length > 0 && (
          <TypeMapping>
            <strong>Current mappings:</strong>
            {allTypes.map((t) => (
              <TypeBadge
                key={t.type_id}
                $isActive={items.some((p) => p.product?.product_type === t.type_id)}
              >
                <code>{t.type_id}</code> → {t.name}
              </TypeBadge>
            ))}
          </TypeMapping>
        )}
        <p>
          <strong>Note:</strong> "006" should automatically show as "james" if types are
          loaded correctly.
        </p>
      </DebugInfo> */}
    </Wrapper>
  );
};

export default PrintCustom;

const Wrapper = styled.div`
  padding: 2rem;
  margin: auto;
  max-width: 1400px;
`;

const DebugButton = styled.button`
  padding: 8px 16px;
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 10px;
  font-weight: bold;

  &:hover {
    background: #c0392b;
  }
`;

const TypeStatus = styled.div`
  color: ${(props) => (props.children.includes('✓') ? '#27ae60' : '#f39c12')};
  font-weight: bold;
  margin-bottom: 1rem;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 4px solid
    ${(props) => (props.children.includes('✓') ? '#27ae60' : '#f39c12')};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 2rem;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  th {
    background: #2c3e50;
    color: white;
    padding: 12px;
    text-align: center;
    font-weight: 600;
    border: 1px solid #1a252f;
  }

  td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: center;
  }

  input {
    width: 100%;
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-sizing: border-box;
    text-align: center;

    &:focus {
      outline: none;
      border-color: #3498db;
      box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
    }
  }
`;

const TypeInput = styled.input`
  font-weight: ${(props) => (props.$isName ? 'bold' : 'normal')};
  color: ${(props) => (props.$isName ? '#27ae60' : '#000')};
  border-color: ${(props) => (props.$isName ? '#27ae60' : '#ccc')};
`;

const TypeCode = styled.div`
  font-size: 0.7rem;
  color: #666;
  margin-top: 2px;
`;

const Buttons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 2rem;

  button {
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 140px;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .btn-cancel {
    background: #95a5a6;
    color: white;
  }

  .btn-debug {
    background: #f39c12;
    color: white;
  }

  .btn-download {
    background: #27ae60;
    color: white;
  }

  .btn-print {
    background: #3498db;
    color: white;
  }

  .btn-save {
    background: #9b59b6;
    color: white;
  }
`;

const PreviewSection = styled.div`
  margin-top: 25px;
  padding: 12px;
  background: #fafafa;
  border: 1px solid #dcdcdc;
  border-radius: 6px;
`;

const LabelRow = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  text-align: center;
  gap: 8px;
`;

const MiniLabel = styled.div`
  width: auto;
  height: 80px;
  border: 1px solid #000;
  display: flex;
  align-items: center; /* centers QR + MiniText block */
  padding: 0px 8px 0px 2px;
  background: #fff;
`;

const MiniQR = styled.div`
  width: 75px;
  height: 75px;
  border: 1px solid #000;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 8px;
`;

const MiniText = styled.div`
  font-family: 'Times New Roman', serif;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 44px;
  line-height: 1.15;
  margin-left: 8px;
  .title {
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;
    text-align: left;
  }

  .details {
    font-size: 13px;
    white-space: nowrap;
    text-align: left;
  }
`;

const DebugInfo = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  background: #e8f4fc;
  border-radius: 8px;
  border-left: 4px solid #3498db;

  p {
    margin: 0.5rem 0;
    font-size: 0.95rem;
    color: #2c3e50;
  }
`;

const TypeMapping = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 10px 0;
`;

const TypeBadge = styled.div`
  padding: 4px 8px;
  background: ${(props) => (props.$isActive ? '#d4edda' : '#f8f9fa')};
  border: 1px solid ${(props) => (props.$isActive ? '#c3e6cb' : '#dee2e6')};
  border-radius: 4px;
  font-size: 0.85rem;

  code {
    background: #e9ecef;
    padding: 1px 4px;
    border-radius: 2px;
    font-weight: bold;
    margin-right: 4px;
  }
`;
